import {
  QueryClient,
  QueryClientProvider as ReactQueryClientProvider,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type {
  FC,
  ReactNode,
} from 'react';
import { useMemo } from 'react';
import { useGlobalErrorContext } from 'shared/context/globalError.js';
import { getMultiModalConfig } from 'shared/utils/multiModalConfig.js';

const useQueryClient = () => {
  const errorContext = useGlobalErrorContext();

  const queryClient = useMemo(
    () => {
      return new QueryClient({
        queryCache: getMultiModalConfig()?.queryCache,
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            throwOnError: (error, query) => {
              if (
                error instanceof AxiosError
                && query.state.errorUpdatedAt
                > (errorContext.error?.updatedAt ?? 0)
              ) {
                // Use setTimeout to break the render cycle and prevent infinite loops
                setTimeout(() => {
                  errorContext.setError({
                    error,
                    updatedAt: query.state.errorUpdatedAt,
                  });
                }, 0);
              }
              return false;
            },
          },
        },
      });
    },
    [],
  );

  return queryClient;
};

export const QueryClientProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const queryClient = useQueryClient();

  return (
    <ReactQueryClientProvider client={queryClient}>
      {children}
    </ReactQueryClientProvider>
  );
};
